import { create } from 'zustand';
import { practiceApi } from '../services/api';
import { BoardResponse, CaseListItem, Task } from '../types';

type CasesScope = 'cases' | 'all';
type TasksScope = 'mine' | 'assigned';

/**
 * Cache for the Practice section (mirrors mattersStore's cache-then-revalidate
 * idea): the board, the merged cases list and the tasks list render instantly
 * from cache while a refresh runs in the background. Mutations happen on the
 * pages (direct API calls) and then call fetchBoard()/fetchCases()/fetchTasks()
 * — or invalidate() to force every panel to refetch on next mount.
 */
interface PracticeState {
  board: BoardResponse | null;
  boardFetchedAt: number | null;
  isFetchingBoard: boolean;
  fetchBoard: () => Promise<void>;

  cases: CaseListItem[];
  casesScope: CasesScope;
  casesFetchedAt: number | null;
  isFetchingCases: boolean;
  fetchCases: (scope?: CasesScope) => Promise<void>;

  tasks: Task[];
  tasksScope: TasksScope;
  tasksFetchedAt: number | null;
  isFetchingTasks: boolean;
  fetchTasks: (scope?: TasksScope) => Promise<void>;

  /** Mark every cache stale (after an assistant action or a mutation). */
  invalidate: () => void;
}

export const usePracticeStore = create<PracticeState>((set, get) => ({
  board: null,
  boardFetchedAt: null,
  isFetchingBoard: false,
  fetchBoard: async () => {
    if (get().isFetchingBoard) return;
    set({ isFetchingBoard: true });
    try {
      const { data } = await practiceApi.board();
      set({ board: data, boardFetchedAt: Date.now(), isFetchingBoard: false });
    } catch {
      set({ isFetchingBoard: false });
    }
  },

  cases: [],
  casesScope: 'cases',
  casesFetchedAt: null,
  isFetchingCases: false,
  fetchCases: async (scope) => {
    const target = scope ?? get().casesScope;
    if (get().isFetchingCases) return;
    // Scope change: keep the stale list on screen, but mark it un-fetched so
    // pages know a first-load for this scope is running.
    if (target !== get().casesScope) set({ casesScope: target, casesFetchedAt: null });
    set({ isFetchingCases: true });
    try {
      const { data } = await practiceApi.listCases(target);
      set({ cases: data, casesFetchedAt: Date.now(), isFetchingCases: false });
    } catch {
      set({ isFetchingCases: false });
    }
  },

  tasks: [],
  tasksScope: 'mine',
  tasksFetchedAt: null,
  isFetchingTasks: false,
  fetchTasks: async (scope) => {
    const target = scope ?? get().tasksScope;
    if (get().isFetchingTasks) return;
    if (target !== get().tasksScope) set({ tasksScope: target, tasksFetchedAt: null });
    set({ isFetchingTasks: true });
    try {
      const { data } = await practiceApi.listTasks(target);
      set({ tasks: data, tasksFetchedAt: Date.now(), isFetchingTasks: false });
    } catch {
      set({ isFetchingTasks: false });
    }
  },

  invalidate: () => set({ boardFetchedAt: null, casesFetchedAt: null, tasksFetchedAt: null }),
}));
